// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { TextFieldProps } from "@insite/mobius/TextField";
import QtyField, { QtyFieldStyles } from "@synd/App/Components/Qty/QtyField";
import xUseQty from "@synd/App/Hooks/xUseQty";
import Select from "@synd/MobiusOverrides/Select";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: QtyFieldStyles;
    displayTooltip?: boolean;
    // XNG-Changes
    showLabel?: boolean;
    showDisabled?: boolean;
    isDebounceDisabled?: boolean;
    variant?: "input" | "select";
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

// export const productQuantityOrderedStyles: TextFieldPresentationProps = {
//     cssOverrides: {
//         formInputWrapper: css`
//             input[type="number"]::-webkit-outer-spin-button,
//             input[type="number"]::-webkit-inner-spin-button {
//                 /* ISC-11023 deal with this. */
//                 /* stylelint-disable-next-line */
//                 -webkit-appearance: none;
//                 margin: 0;
//             }
//         `,
//         inputSelect: css`
//             /* ISC-11023 deal with this. */
//             /* stylelint-disable-next-line */
//             -moz-appearance: textfield;
//         `,
//     },
// };
export const productQuantityOrderedStyles: QtyFieldStyles = {};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, detail, minimumOrderQty = 1 },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    // XNG-Change
    displayTooltip = true,
    showLabel = false,
    showDisabled,
    isDebounceDisabled,
    variant,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [minQtyState, setMinQty] = useState(1);
    const [maxQtyState, setMaxQty] = useState(999999);

    const showQtyTooltip =
        ((minimumOrderQty && minimumOrderQty > 1) || (detail?.multipleSaleQty && detail?.multipleSaleQty > 1)) &&
        displayTooltip;

    useEffect(() => {
        if (detail?.multipleSaleQty && detail?.multipleSaleQty > 1) {
            const maxQty = 999999 - (999999 % detail.multipleSaleQty);
            setMaxQty(maxQty);
        }
    }, [detail?.multipleSaleQty]);

    // Xng-Changes
    const { handleChange, debouncedHandleChange } = xUseQty({
        minQtyState,
        multipleSaleQty: detail?.multipleSaleQty && Number(detail?.multipleSaleQty),
        onQtyOrderedChanged,
        productId,
        setProductQty,
        timeout: isDebounceDisabled && !showQtyTooltip ? 0 : 300,
    });

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        // Xng-Changes
        debouncedHandleChange(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
    }, [qtyOrdered]);

    // Xng-Changes
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, 1);
        const qty =
            qtyOrdered === 1 && detail?.multipleSaleQty && detail?.multipleSaleQty > 1
                ? Math.max(minQty, detail?.multipleSaleQty)
                : Math.max(minQty, qtyOrdered);
        debouncedHandleChange(qty.toString());
        setMinQty(minQty);
    }, [minimumOrderQty]);
    // XNG-Change Show disabled
    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }
    const isDisabled = (!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart || showDisabled;

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement> | string) => {
        // XNG-Change: accepting string qty values
        const value = typeof event === "string" ? event : event.target.value;
        setProductQty(value);
        if (variant === "select") {
            onQtyOrderedChanged?.(+value);
        }
    };

    // Xng-Changes
    const blurHandler = () => {
        if (Number(productQty) < minQtyState) {
            setProductQty(`${minQtyState}`);
            debouncedHandleChange(`${minQtyState}`);
        } else {
            handleChange(productQty);
        }
    };

    // XNG-Changes
    if (variant === "select") {
        return (
            <Select
                value={productQty}
                onChange={qtyChangeHandler}
                data-test-selector="product_qtyOrdered"
                sizeVariant="small"
                disabled={isDisabled}
                {...styles.select}
            >
                {Array.from({ length: 101 - minimumOrderQty }, (_, i) => minimumOrderQty + i).map(num => (
                    <option key={num} value={num}>
                        {num}
                    </option>
                ))}
            </Select>
        );
    }

    // XNG-Change use QtyField
    return (
        // <TextField
        //     label={labelOverride ?? translate("QTY")}
        //     value={productQty}
        //     type="number"
        //     min={0}
        //     max={999999}
        //     data-test-selector="product_qtyOrdered"
        //     {...otherProps}
        //     onKeyPress={keyPressHandler}
        //     onChange={qtyChangeHandler}
        //     {...styles}
        // />
        <QtyField
            label={labelOverride}
            showLabel={showLabel}
            value={productQty}
            type="qty"
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={blurHandler}
            showMultipleSaleQtyTooltip={!!showQtyTooltip}
            minimumOrderQty={minimumOrderQty}
            multipleSaleQty={detail?.multipleSaleQty}
            extendedStyles={styles}
            min={Math.max(minQtyState, detail?.multipleSaleQty || 1)}
            max={maxQtyState}
            disabled={isDisabled}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
